import { useCallback, useEffect, useMemo, useState } from 'react';
import { BrainCircuit, Loader2, RefreshCw, RotateCcw, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import AdminTenantScopeBar from '@/components/AdminTenantScopeBar';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

type RuleNumberKey =
  | 'segmentStrategicValue'
  | 'segmentGrowthValue'
  | 'riskWatchThreshold'
  | 'riskRecoverThreshold'
  | 'recentVisitWindowDays'
  | 'staleDealWindowDays'
  | 'creditUtilizationWarningPct'
  | 'disciplineCoverageTargetPct'
  | 'maxPlaybookItems'
  | 'penaltyPerOverdueFollowUp'
  | 'penaltyPerStaleDeal'
  | 'penaltyPerOverdueArItem'
  | 'penaltyHighCreditUtilization'
  | 'penaltyMissingRecentVisit'
  | 'penaltyMissingNextStep'
  | 'penaltyLowOwnerCoverage'
  | 'bonusHealthyAr'
  | 'bonusRecentVisit'
  | 'bonusDisciplineCoverage';

type EffectiveRules = Record<RuleNumberKey | 'playbookTemplates', number | Array<Record<string, unknown>>>;

interface CrmRulePayload {
  key: string;
  tenantId: string;
  tenantName: string;
  override: Record<string, unknown> | null;
  defaultRules: EffectiveRules;
  effectiveRules: EffectiveRules;
  updated_at: string | null;
}

const NUMERIC_FIELDS: Array<{ key: RuleNumberKey; label: string; hint: string }> = [
  { key: 'segmentStrategicValue', label: 'Strategic Value Threshold', hint: '高于此值进入 strategic segment' },
  { key: 'segmentGrowthValue', label: 'Growth Value Threshold', hint: '高于此值且健康时进入 growth' },
  { key: 'riskWatchThreshold', label: 'Watch Risk Threshold', hint: '达到此分数进入 watchlist' },
  { key: 'riskRecoverThreshold', label: 'Recover Risk Threshold', hint: '达到此分数进入 recovery' },
  { key: 'recentVisitWindowDays', label: 'Recent Visit Window Days', hint: '最近触达窗口' },
  { key: 'staleDealWindowDays', label: 'Stale Deal Window Days', hint: '商机多久未更新视为 stale' },
  { key: 'creditUtilizationWarningPct', label: 'Credit Warning %', hint: '信用占用预警百分比' },
  { key: 'disciplineCoverageTargetPct', label: 'Discipline Coverage Target %', hint: 'next-step 覆盖率目标' },
  { key: 'maxPlaybookItems', label: 'Max Playbook Items', hint: 'Account360 最多显示几条 playbook' },
  { key: 'penaltyPerOverdueFollowUp', label: 'Penalty Per Overdue Follow-Up', hint: '每条 overdue follow-up 扣分' },
  { key: 'penaltyPerStaleDeal', label: 'Penalty Per Stale Deal', hint: '每条 stale deal 扣分' },
  { key: 'penaltyPerOverdueArItem', label: 'Penalty Per Overdue AR Item', hint: '每条逾期 AR 扣分' },
  { key: 'penaltyHighCreditUtilization', label: 'Penalty High Credit Utilization', hint: '信用占用过高扣分' },
  { key: 'penaltyMissingRecentVisit', label: 'Penalty Missing Recent Visit', hint: '最近没拜访扣分' },
  { key: 'penaltyMissingNextStep', label: 'Penalty Missing Next Step', hint: '缺少 next step 扣分' },
  { key: 'penaltyLowOwnerCoverage', label: 'Penalty Low Owner Coverage', hint: 'owner coverage 不足扣分' },
  { key: 'bonusHealthyAr', label: 'Bonus Healthy AR', hint: 'AR 健康加分' },
  { key: 'bonusRecentVisit', label: 'Bonus Recent Visit', hint: '最近有触达加分' },
  { key: 'bonusDisciplineCoverage', label: 'Bonus Discipline Coverage', hint: 'discipline 达标加分' },
];

type FormState = Record<RuleNumberKey, string> & {
  playbookTemplates: string;
  rawOverride: string;
};

type CrmProfileKey = 'b2b_services' | 'product' | 'logistics' | 'freight';

type NavConfigValue = Record<string, unknown> & {
  crmProfiles?: CrmProfileKey[];
  crmTerminology?: Record<string, string>;
};

const CRM_PROFILE_OPTIONS: Array<{ key: CrmProfileKey; label: string; hint: string }> = [
  { key: 'b2b_services', label: 'B2B Services', hint: 'Recurring service, renewal, SLA, customer success' },
  { key: 'product', label: 'Product Sales', hint: 'Product quote, reorder, warranty, replacement cycle' },
  { key: 'logistics', label: 'Logistics Services', hint: 'Transport, warehouse, lane, service exception' },
  { key: 'freight', label: 'Freight Forwarding', hint: 'RFQ, trade lane, shipment exception, shipper growth' },
];

function buildOverride(form: FormState): Record<string, unknown> {
  const override: Record<string, unknown> = {
    $schema: 'boox-rules-override/v1',
    domain: 'crm-customer-intelligence',
    description: 'Tenant RM customer intelligence override generated from the platform admin UI.',
    overrides: {},
  };
  const rules = override.overrides as Record<string, unknown>;

  for (const field of NUMERIC_FIELDS) {
    const parsed = Number(form[field.key]);
    if (Number.isFinite(parsed)) {
      rules[field.key] = { values: parsed };
    }
  }

  const templates = JSON.parse(form.playbookTemplates);
  rules.playbookTemplates = { values: templates };

  return override;
}

function formFromRules(effectiveRules: EffectiveRules, override: Record<string, unknown> | null): FormState {
  const form = {} as FormState;
  for (const field of NUMERIC_FIELDS) {
    form[field.key] = String(effectiveRules[field.key] ?? '');
  }
  form.playbookTemplates = JSON.stringify(effectiveRules.playbookTemplates ?? [], null, 2);
  form.rawOverride = JSON.stringify(override ?? {}, null, 2);
  return form;
}

export default function CrmIntelligenceRulesPage() {
  const { user } = useAuth();
  const canManageRules = user?.role === 'platform_admin';
  const [scopeTenantId, setScopeTenantId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [ruleData, setRuleData] = useState<CrmRulePayload | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [navConfigValue, setNavConfigValue] = useState<NavConfigValue>({});
  const [crmProfiles, setCrmProfiles] = useState<CrmProfileKey[]>([]);
  const [crmTerminologyJson, setCrmTerminologyJson] = useState('{}');
  const [profileSaving, setProfileSaving] = useState(false);

  const loadCrmProfileConfig = useCallback(async (tenantId: string) => {
    const key = `nav:tenant:${tenantId}:config`;
    try {
      const savedRaw = await api.get<NavConfigValue | { value?: NavConfigValue }>(`/domain-configs/${encodeURIComponent(key)}`);
      const saved = (typeof savedRaw === 'object' && savedRaw !== null && 'value' in savedRaw)
        ? savedRaw.value ?? {}
        : savedRaw;
      const value = (saved && typeof saved === 'object' ? saved : {}) as NavConfigValue;
      setNavConfigValue(value);
      setCrmProfiles(Array.isArray(value.crmProfiles) ? value.crmProfiles : []);
      setCrmTerminologyJson(JSON.stringify(value.crmTerminology ?? {}, null, 2));
    } catch {
      setNavConfigValue({});
      setCrmProfiles([]);
      setCrmTerminologyJson('{}');
    }
  }, []);

  const load = useCallback(async (tenantId: string) => {
    setLoading(true);
    try {
      await loadCrmProfileConfig(tenantId);
      if (canManageRules) {
        const response = await api.get<{ item: CrmRulePayload }>(`/platform/crm-intelligence-rules?tenantId=${encodeURIComponent(tenantId)}`);
        setRuleData(response.item);
        setForm(formFromRules(response.item.effectiveRules, response.item.override));
      } else {
        setRuleData(null);
        setForm(null);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '加载 RM intelligence rules 失败');
    } finally {
      setLoading(false);
    }
  }, [canManageRules, loadCrmProfileConfig]);

  useEffect(() => {
    if (canManageRules || scopeTenantId || !user?.tenantId) return;
    setScopeTenantId(user.tenantId);
  }, [canManageRules, scopeTenantId, user?.tenantId]);

  useEffect(() => {
    if (!scopeTenantId) {
      setRuleData(null);
      setForm(null);
      return;
    }
    void load(scopeTenantId);
  }, [load, scopeTenantId]);

  const generatedOverride = useMemo(() => {
    if (!form) return '{}';
    try {
      return JSON.stringify(buildOverride(form), null, 2);
    } catch {
      return '{\n  "error": "Invalid playbook templates JSON"\n}';
    }
  }, [form]);

  const handleSave = useCallback(async () => {
    if (!scopeTenantId || !form) {
      toast.error('请先选择 tenant');
      return;
    }

    let override: Record<string, unknown>;
    try {
      override = buildOverride(form);
    } catch {
      toast.error('Playbook templates 必须是合法 JSON 数组');
      return;
    }

    setSaving(true);
    try {
      await api.post('/platform/crm-intelligence-rules', {
        tenantId: scopeTenantId,
        override,
      });
      toast.success('RM intelligence rules 已保存');
      await load(scopeTenantId);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '保存失败');
    } finally {
      setSaving(false);
    }
  }, [form, load, scopeTenantId]);

  const handleDelete = useCallback(async () => {
    if (!scopeTenantId) return;
    if (!confirm('删除该 tenant 的 RM intelligence override？删除后会回退到 domain 默认规则。')) return;

    setDeleting(true);
    try {
      await api.delete(`/platform/crm-intelligence-rules/${encodeURIComponent(scopeTenantId)}`);
      toast.success('RM intelligence override 已删除');
      await load(scopeTenantId);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '删除失败');
    } finally {
      setDeleting(false);
    }
  }, [load, scopeTenantId]);

  const handleResetToLoaded = useCallback(() => {
    if (!ruleData) return;
    setForm(formFromRules(ruleData.effectiveRules, ruleData.override));
  }, [ruleData]);

  const toggleCrmProfile = useCallback((profile: CrmProfileKey) => {
    setCrmProfiles((current) => (
      current.includes(profile)
        ? current.filter((item) => item !== profile)
        : [...current, profile]
    ));
  }, []);

  const handleSaveCrmProfiles = useCallback(async () => {
    if (!scopeTenantId) {
      toast.error('请先选择 tenant');
      return;
    }

    let crmTerminology: Record<string, string>;
    try {
      const parsed = JSON.parse(crmTerminologyJson);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('invalid');
      crmTerminology = parsed as Record<string, string>;
    } catch {
      toast.error('RM terminology 必须是合法 JSON object');
      return;
    }

    setProfileSaving(true);
    try {
      const key = `nav:tenant:${scopeTenantId}:config`;
      const nextValue: NavConfigValue = {
        ...navConfigValue,
        crmProfiles,
        crmTerminology,
      };
      await api.put(`/domain-configs/${encodeURIComponent(key)}`, nextValue);
      setNavConfigValue(nextValue);
      toast.success('RM profile config 已保存；刷新后菜单和 RM Signals 会按 profile 更新');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '保存 RM profile config 失败');
    } finally {
      setProfileSaving(false);
    }
  }, [crmProfiles, crmTerminologyJson, navConfigValue, scopeTenantId]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <BrainCircuit className="w-6 h-6 text-emerald-600" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">RM Intelligence Rules</h1>
            <p className="text-sm text-gray-500">Tenant 级 account scoring / segment / playbook 配置</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canManageRules ? (
            <AdminTenantScopeBar value={scopeTenantId} onChange={setScopeTenantId} showRingBadge />
          ) : (
            <div className="rounded-md border bg-white px-3 py-2 text-xs text-gray-600">
              Tenant config: {scopeTenantId ?? user?.tenantId ?? 'current tenant'}
            </div>
          )}
          <button
            type="button"
            onClick={() => scopeTenantId && void load(scopeTenantId)}
            className="p-2 rounded hover:bg-gray-100"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {!scopeTenantId && (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white px-6 py-10 text-center text-sm text-gray-500">
          先选择一个 tenant，再编辑该 tenant 的 RM intelligence override。
        </div>
      )}

      {scopeTenantId && loading && !form && crmProfiles.length === 0 && (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      )}

      {scopeTenantId && (
        <>
          {canManageRules && form && ruleData ? (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <div className="rounded-xl border bg-white p-4 xl:col-span-2">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-semibold text-gray-900">Rule Controls</h2>
                  <p className="text-xs text-gray-500">
                    当前 tenant: {ruleData.tenantName} {ruleData.updated_at ? `· 最后更新 ${new Date(ruleData.updated_at).toLocaleString('zh-CN')}` : '· 当前无 override'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                {NUMERIC_FIELDS.map((field) => (
                  <label key={field.key} className="rounded-lg border border-gray-200 p-3">
                    <span className="block text-xs font-medium text-gray-700">{field.label}</span>
                    <span className="mt-1 block text-[11px] text-gray-500">{field.hint}</span>
                    <input
                      type="number"
                      value={form[field.key]}
                      onChange={(event) => setForm((current) => current ? { ...current, [field.key]: event.target.value } : current)}
                      className="mt-2 w-full rounded border border-gray-300 px-2.5 py-2 text-sm"
                    />
                    <span className="mt-2 block text-[11px] text-gray-400">
                      Default: {String(ruleData.defaultRules[field.key] ?? '—')}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="rounded-xl border bg-emerald-50 p-4">
              <h2 className="text-sm font-semibold text-emerald-900">Save / Reset</h2>
              <p className="mt-1 text-xs text-emerald-800">
                保存后会写入 `platform_settings` 的 tenant override，并清空 rule cache，让 Account360 立刻读到新规则。
              </p>
              <div className="mt-4 space-y-2">
                <button
                  type="button"
                  onClick={() => void handleSave()}
                  disabled={saving}
                  className="flex w-full items-center justify-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  保存 Override
                </button>
                <button
                  type="button"
                  onClick={handleResetToLoaded}
                  className="flex w-full items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <RotateCcw className="w-4 h-4" />
                  回到已加载版本
                </button>
                <button
                  type="button"
                  onClick={() => void handleDelete()}
                  disabled={deleting || !ruleData.override}
                  className="flex w-full items-center justify-center gap-2 rounded-md border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  删除 Tenant Override
                </button>
              </div>
            </div>
          </div>
          ) : null}

          <div className="rounded-xl border bg-white p-4">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">RM Profile Composition</h2>
                <p className="text-xs text-gray-500">
                  控制 RM 菜单术语、Signals demo story 和行业信号 pattern。可多选；留空则由 tenant 名称和启用模块自动推断。
                </p>
              </div>
              <button
                type="button"
                onClick={() => void handleSaveCrmProfiles()}
                disabled={profileSaving}
                className="flex items-center justify-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
              >
                {profileSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                保存 RM Profile
              </button>
            </div>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              {CRM_PROFILE_OPTIONS.map((option) => {
                const checked = crmProfiles.includes(option.key);
                return (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => toggleCrmProfile(option.key)}
                    className={`rounded-lg border p-3 text-left transition ${
                      checked
                        ? 'border-slate-900 bg-slate-900 text-white'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-slate-400'
                    }`}
                  >
                    <span className="block text-sm font-semibold">{option.label}</span>
                    <span className={`mt-1 block text-xs ${checked ? 'text-slate-200' : 'text-gray-500'}`}>{option.hint}</span>
                  </button>
                );
              })}
            </div>
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-600">Recommended combinations</h3>
                <ul className="mt-2 space-y-1 text-xs text-gray-600">
                  <li>Stamford Tyre: Product Sales + B2B Services</li>
                  <li>Freight forwarder: Freight Forwarding + Logistics Services + B2B Services</li>
                  <li>Trucker / local logistics: Logistics Services + B2B Services</li>
                </ul>
              </div>
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">RM terminology override JSON</span>
                <textarea
                  value={crmTerminologyJson}
                  onChange={(event) => setCrmTerminologyJson(event.target.value)}
                  rows={6}
                  className="mt-2 w-full rounded border border-gray-300 px-3 py-2 font-mono text-xs"
                  placeholder='{"account":"Fleet / B2B Accounts","quote":"Product / Service Quotes"}'
                />
              </label>
            </div>
          </div>

          {canManageRules && form && ruleData ? (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <div className="rounded-xl border bg-white p-4">
              <div className="mb-3">
                <h2 className="text-sm font-semibold text-gray-900">Playbook Templates</h2>
                <p className="text-xs text-gray-500">编辑 sales / ops / finance 的 next-playbook 模板数组</p>
              </div>
              <textarea
                value={form.playbookTemplates}
                onChange={(event) => setForm((current) => current ? { ...current, playbookTemplates: event.target.value } : current)}
                rows={16}
                className="w-full rounded border border-gray-300 px-3 py-2 font-mono text-xs"
              />
            </div>

            <div className="space-y-4">
              <div className="rounded-xl border bg-white p-4">
                <div className="mb-3">
                  <h2 className="text-sm font-semibold text-gray-900">Generated Override Preview</h2>
                  <p className="text-xs text-gray-500">页面表单会生成这个 tenant override JSON</p>
                </div>
                <textarea
                  value={generatedOverride}
                  readOnly
                  rows={16}
                  className="w-full rounded border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-xs text-gray-700"
                />
              </div>

              <div className="rounded-xl border bg-white p-4">
                <div className="mb-3">
                  <h2 className="text-sm font-semibold text-gray-900">Current Stored Override</h2>
                  <p className="text-xs text-gray-500">数据库里当前保存的原始 override</p>
                </div>
                <textarea
                  value={form.rawOverride}
                  readOnly
                  rows={10}
                  className="w-full rounded border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-xs text-gray-700"
                />
              </div>
            </div>
          </div>
          ) : null}
        </>
      )}
    </div>
  );
}
